/**
 * Lazy-loaded `toast` API for sonner (BS-2161).
 *
 * Direct `import { toast } from 'sonner'` pulls the whole sonner module
 * (~68 KB) into whichever chunk the importer ends up in. When the importer
 * is `lib/providers/form-context.tsx` (used by the root layout), sonner
 * gets bundled into First Load JS for every route - including `/`,
 * `/_not-found`, and `/gsap` which never call `toast()`.
 *
 * `toastLazy` defers the import until the first `toast.*()` call. Once
 * loaded, the module is cached for the rest of the session, so subsequent
 * calls are synchronous (single promise resolution on first call only).
 *
 * The visible behavior change is a ~1-tick delay on the very first toast
 * (typically <100ms because the chunk is small and the call site already
 * runs after hydration). Users experience no perceptible change; the
 * tradeoff is significant First Load JS reduction across every route.
 *
 * Returns the toast function's return type as `void`. The original sonner
 * `toast()` returns a string id used by `toast.dismiss(id)` - we are not
 * using that workflow anywhere in OLA, so dropping the return is safe.
 * If a caller ever needs the id, swap that single call to import sonner
 * directly.
 */

type ToastModule = typeof import('sonner');
type SonnerToast = ToastModule['toast'];

let cached: SonnerToast | undefined;
let pending: Promise<SonnerToast> | undefined;

function loadToast(): Promise<SonnerToast> {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = import('sonner').then((mod) => {
      cached = mod.toast;
      return mod.toast;
    });
  }
  return pending;
}

/**
 * Returns a Promise that resolves once the toast has actually fired. The
 * promise rejects if the dynamic `import('sonner')` chunk fails to load
 * (a real failure mode during deploys or on flaky networks). Callers can
 * `await` the returned promise when "the toast actually showed" matters
 * for correctness (e.g. recording a one-time suppression marker).
 *
 * Sync callers that don't care about delivery can ignore the return.
 */
function call(method: 'info' | 'error' | 'success' | 'warning', message: string): Promise<void> {
  if (cached) {
    cached[method](message);
    return Promise.resolve();
  }
  return loadToast().then((t) => {
    t[method](message);
  });
}

export const toastLazy = {
  info: (message: string) => call('info', message),
  error: (message: string) => call('error', message),
  success: (message: string) => call('success', message),
  warning: (message: string) => call('warning', message),
};
